import React from 'react';
import googleAiOverviewLogo from '../../../../images/engines/google-ai-overview.svg';
import openAiChatGptLogo from '../../../../images/engines/openai-chatgpt.webp';

/**
 * The engines this app carries a logo for, by taxonomy term key.
 *
 * An engine is recognised by its mark long before its name is read, so the mark is what the product
 * shows. The map is deliberately small and explicit: a logo is a third party's trademark, shipped
 * here as a file, so an engine appears in it only once someone has put its file in
 * `src/images/engines/`. Every other engine — including one the backend adds tomorrow — renders the
 * way it renders today, which is why nothing downstream may assume a logo exists.
 *
 * Keys are the term keys Sync Core seeds the `engine` facet with, matched case-insensitively so a
 * key that arrives differently cased still finds its mark.
 */
const ENGINE_LOGO_BY_TERM_KEY: Readonly<Record<string, string>> = {
  'engine.openai.chatgpt': openAiChatGptLogo,
  'engine.google.ai-overview': googleAiOverviewLogo,
};

/** The logo file for an engine term key, or `undefined` where this app carries none. */
export function engineLogoSource(termKey: string | undefined): string | undefined {
  const key = String(termKey ?? '')
    .trim()
    .toLowerCase();

  return key ? ENGINE_LOGO_BY_TERM_KEY[key] : undefined;
}

/**
 * Whether an engine term renders as its mark rather than as a label.
 *
 * Exported because the decision has to be made before the logo is rendered: a surface that puts a
 * mark in front of a name has to know whether there is a mark, and a surface that replaces a badge
 * with one has to know whether it still needs the badge.
 */
export function hasEngineLogo(termKey: string | undefined): boolean {
  return !!engineLogoSource(termKey);
}

/**
 * How big the mark is drawn, named by the place it is drawn in rather than by a number — the point
 * is that the logo matches the line it sits on, and a call site passing its own pixel value is how
 * that stops being true.
 *
 * - `badge` — standing in for a term badge in a row of them. Sized just under the badges beside it
 *   (`text-[10px]` inside `px-2 py-0.5`, so 22-26px tall depending on the row's line height), so a
 *   mark reads as one of the row rather than as something taller than it.
 * - `name` — in front of a term name in a list (`text-base`, 24px line box).
 * - `heading` — in front of a term name on the term's own page (`text-lg`, 28px line box).
 */
export type EngineLogoSize = 'badge' | 'name' | 'heading';

const ENGINE_LOGO_SIZE_CLASS: Readonly<Record<EngineLogoSize, string>> = {
  badge: 'h-[18px] w-[18px]',
  name: 'h-5 w-5',
  heading: 'h-6 w-6',
};

// type-duplication-approved: EngineLogo renders a passive taxonomy-keyed image, not a clickable control, and IProps here is Button's own per-component prop interface, never re-exported by name from the package: name is the accessible tooltip label with no counterpart on Button, size is EngineLogoSize ('badge' | 'name' | 'heading', the row it sits in) rather than Button's 'sm' | 'md' | 'lg' size enum, and only className is genuinely the shared styling hook. The property names coincide; the shapes do not.
export interface IEngineLogoProps {
  /** The engine's taxonomy term key. A key this app carries no logo for renders nothing at all. */
  termKey: string | undefined;
  /**
   * The engine's name, for a reader who cannot see the mark.
   *
   * Pass it where the logo stands IN PLACE of the name — there the mark is the only thing naming
   * the engine. Leave it out where the name is rendered beside the logo, so the mark is announced
   * as decoration instead of reading the same engine out twice.
   */
  name?: string;
  size?: EngineLogoSize;
  className?: string;
}

/**
 * One engine's logo.
 *
 * Renders nothing for an engine this app carries no logo for, so a call site can place it
 * unconditionally and let the engine decide — which is what keeps the fallback honest: every
 * surface that shows a mark also has to keep working for an engine that has none.
 */
export function EngineLogo({ termKey, name, size = 'badge', className }: IEngineLogoProps) {
  const source = engineLogoSource(termKey);

  if (!source) {
    return null;
  }

  return (
    <img
      src={source}
      // Empty where the name is already on screen: that is what marks the image as decoration.
      alt={name ?? ''}
      className={['inline-block shrink-0 object-contain align-middle', ENGINE_LOGO_SIZE_CLASS[size], className].filter(Boolean).join(' ')}
    />
  );
}
